/**
 * Walking worker functionality for adventure locations
 */

GameWorker.prototype.ensureViewExists = function () {
    if (!this.view) {
        Map2d.currentMap.workers.onAddWorker(this);
    }
};

GameWorker.prototype.onGetView = function () {
    return this.view;
};

GameWorker.prototype.highlight = function () {
    return this.view && this.view.highlight();
};

GameWorker.prototype.unhighlight = function () {
    return this.view && this.view.unhighlight();
};

GameWorker.prototype.walkNextToUnit = function (unit, callback) {
    const candidates = [];
    const unitCells = [cc.p(unit.x, unit.y)];
    let minDistance = 0;

    if (unit.isMultiCell()) {
        const multicell = unit.findComponent(MultiCell);
        for (const delta of multicell.shape) {
            if (!(delta.x === 0 && delta.y === 0)) {
                unitCells.push(cc.p(unit.x + delta.x, unit.y + delta.y));
            }
        }
        minDistance = 1;
    }

    ["w", "e", "n", "s"].forEach((dir) => {
        const region = Map2d.currentMap.regions[`worker_dialogue_slots_${dir}`];
        if (region) {
            region.positions.forEach((cell) => {
                if (Map2d.currentMap.isScreenCell(cell) && GameWorker.canWalk(cell)) {
                    candidates.push({ cell, direction: dir });
                }
            });
        }
    });

    const dialogueRegion = Map2d.currentMap.regions.worker_dialogue_slots;
    if (dialogueRegion) {
        dialogueRegion.positions.forEach((cell) => {
            if (Map2d.currentMap.isScreenCell(cell) && GameWorker.canWalk(cell)) {
                candidates.push({ cell });
            }
        });
    }

    if (!candidates.length) {
        Map2d.currentMap.iterateBfs(unit.x, unit.y, (pos) => {
            if (pos.step > 6) {
                return true;
            }
            if (unitCells.every((point) => {
                const distance = cc.pDistance(point, cc.p(pos.x, pos.y));
                return distance >= minDistance && distance <= 3;
            }) && GameWorker.canWalk(pos)) {
                candidates.push({ cell: pos });
            }
        });
    }

    if (candidates.length) {
        const center = Map2d.currentMap.getScreenCenterCell();
        if (center) {
            const centerScreenX = center.x + center.y;
            candidates.sort((a, b) => Math.abs((a.cell.x + a.cell.y) - centerScreenX) - Math.abs((b.cell.x + b.cell.y) - centerScreenX));
        }
        this.ensureViewExists();
        if (this.view) {
            const best = candidates[0];
            this.startMoving(this.findWalkingPath(best.cell, { dialogueTeleport: true }), undefined, {
                direction: best.direction || "w",
                callback
            });
        } else {
            callback();
        }
    } else {
        callback();
    }
};

GameWorker.prototype.findCellsNearUnit = function (unit, any) {
    const directions = [Iso.DOWN_RIGHT, Iso.UP_LEFT, Iso.UP_RIGHT, Iso.DOWN_LEFT];
    let cells = {};

    unit.getShape().forEach((part) => {
        directions.forEach((dir) => {
            const x = unit.x + part.x + dir.x;
            const y = unit.y + part.y + dir.y;

            if (!cells[`${x}_${y}`]) {
                cells[`${x}_${y}`] = { x, y };
            }
        });
    });

    cells = Object.values(cells);

    if (this.position) {
        cells.sort((a, b) => cc.pDistanceSQ(this.position, a) - cc.pDistanceSQ(this.position, b));
    }

    if (any) {
        cells.sort((a, b) => Boolean(GameWorker.canMove(b)) - Boolean(GameWorker.canMove(a)));
        return cells;
    }

    return cells.filter((cell) => GameWorker.canMove(cell));
};

GameWorker.prototype.setPositionNearUnit = function (unit, fallbackCell) {
    if (!Map2d.currentMap.workers.canWalk()) {
        return;
    }

    if (unit) {
        this.position = this.findCellsNearUnit(unit, true)[0];
    }

    if (!this.position) {
        this.position = cc.p(fallbackCell);
    }
};

GameWorker.prototype.findMoveToCells = function (unit, start) {
    if (start || !Map2d.currentMap.workers.canWalk()) {
        return undefined;
    }

    const moveToCells = this.findCellsNearUnit(unit);
    if (!moveToCells.length) {
        cleverapps.centerHint.createTextHint(Messages.get("Workers.freeSpace"));
        return null;
    }

    return moveToCells;
};

GameWorker.prototype.findWalkingPath = function (target, options) {
    options = options || {};

    let fallback = this.findNearestAvailableCell(target);

    if (options.teleport) {
        return [fallback || target];
    }

    this.ensureViewExists();

    const from = this.view.getViewPosition();
    if (!from) {
        return [fallback || target];
    }
    const rounded = cc.p(Math.round(from.x), Math.round(from.y));
    const pathfinder = new Pathfinder({
        cost: (x, y, fromX, fromY) => {
            if (!GameWorker.canWalk(cc.p(x, y))) {
                return Pathfinder.IMPASSABLE;
            }
            if (!this.view.hasWalkDirection(cc.p(fromX, fromY), cc.p(x, y))) {
                return Pathfinder.IMPASSABLE;
            }
            return 1;
        },
        stopBefore: options.stopBefore
    });
    const path = pathfinder.findPath(rounded, target, true);

    if (options.teleportFromFar && path.some((cell) => !Map2d.currentMap.isScreenCell(cell))) {
        return [fallback || target];
    }

    if (options.dialogueTeleport && path.length > GameWorker.DIALOGUE_TELEPORT_THRESHOLD) {
        return [fallback || target];
    }

    if (path.length < 2) {
        if (options.shapeCells && options.shapeCells.length > 1) {
            let currentDistance = cc.pDistance(from, fallback);
            options.shapeCells.forEach((shapeCell) => {
                const targetCell = this.findNearestAvailableCell(shapeCell);
                const distance = cc.pDistance(from, targetCell);
                if (distance < currentDistance) {
                    currentDistance = distance;
                    fallback = targetCell;
                }
            });
        }
        return [fallback || target];
    }

    path[0] = from;
    return path;
};

GameWorker.prototype.startMoving = function (path, cellShift, options) {
    options = options || {};
    if (path.length > GameWorker.RUN_THRESHOLD) {
        options.run = true;
    }
    this.showPathDots(path);
    this.path = path.reverse();
    this.position = cc.p(this.path[0]);

    Map2d.currentMap.workers.save();

    this.onUpdateState();
    this.makeStep(cellShift, true, options);
    this.lastCellShift = cellShift;
};

GameWorker.prototype.showPathDots = function (path) {
    if (this.view) {
        this.view.showPathDots(path);
    }
};

GameWorker.prototype.makeStep = function (cellShift, isFirstStep, options) {
    options = options || {};
    cellShift = cellShift || cc.p(0, 0);
    if (!this.path) {
        this.view.dashToCell(cc.pAdd(this.position, cellShift, options.direction));
        return;
    }

    if (this.path.length <= 1) {
        this.path = undefined;

        const completeMove = () => {
            this.view.dashToCell(cc.pAdd(this.position, cellShift), options.direction);
            this.view.stopMoving();

            if (this.unit) {
                this.startWorking();
            }

            if (options.callback) {
                options.callback();
            }
        };

        if (isFirstStep && (!this.lastCellShift || cc.pLength(cc.pSub(this.lastCellShift, cellShift)) > 0.5)) {
            this.view.moveToCell(this.position, cc.pAdd(this.position, cellShift), completeMove, options.direction, options.run);
        } else {
            completeMove();
        }

        return;
    }

    let skipped = false;

    while (this.path.length >= 3) {
        if (this.path.slice(-3).some((cell) => Map2d.currentMap && Map2d.currentMap.isScreenCell(cell))) {
            break;
        }

        skipped = true;
        this.path.pop();
    }

    if (skipped) {
        this.view.dashToCell(cc.pAdd(this.path[this.path.length - 1], cellShift));
    }

    const currentCell = this.path.pop();
    let nextCell = this.path[this.path.length - 1];
    let direction = null;
    if (this.path.length < 2) {
        nextCell = cc.pAdd(nextCell, cellShift);
        direction = options.direction;
    }
    this.view.moveToCell(currentCell, nextCell, () => {
        this.makeStep(cellShift, false, options);
    }, direction, options.run);

    if (this.path.length === 1) {
        this.path.pop();
    }
};

GameWorker.prototype.move = function (cell, cellShift) {
    this.ensureViewExists();

    if (!this.view) {
        return true;
    }

    const bestMoveCell = this.findNearestAvailableCell(cell);
    if (!bestMoveCell) {
        return false;
    }

    this.startMoving(this.findWalkingPath(bestMoveCell), cellShift);

    return cc.pointEqualToPoint(cell, bestMoveCell);
};

GameWorker.canWalk = function (cell) {
    const map = Map2d.currentMap;

    if (map.getFog(cell.x, cell.y)) {
        return false;
    }

    let unit = map.getUnit(cell.x, cell.y);
    if (unit) {
        unit = unit.head || unit;

        const multicell = unit.findComponent(MultiCell);
        if (multicell) {
            return multicell.canMoveThrough(cell.x, cell.y) && UnitsLibrary.LastStage(unit.code).stage === unit.stage;
        }

        if (unit.isGrounded() || unit.isStopsFog() || unit.isImpassable()) {
            return false;
        }

        return true;
    }

    if (!map.isGround(cell.x, cell.y) && !map.isWaterUnit(cell.x, cell.y)) {
        return false;
    }

    return true;
};

GameWorker.RUN_THRESHOLD = 5;
GameWorker.DIALOGUE_TELEPORT_THRESHOLD = 5;
